import { useAuth0 } from '@auth0/auth0-vue'
import { until } from '@vueuse/core'
import { watch } from 'vue'

import { useClinicStore, useProfileStore } from '@/stores'

import { setSentryUser } from './sentry'

/**
 * Initialize Sentry context synchronization with stores
 * This should be called once during app initialization to set up watchers
 * that keep Sentry context in sync with profile and clinic changes.
 *
 * This approach avoids circular dependencies between stores by having
 * a separate initialization layer that watches both stores.
 *
 * Only initializes stores when user is authenticated to avoid triggering
 * API calls on unauthenticated pages.
 */
export async function initSentryContext() {
  const { isAuthenticated, isLoading } = useAuth0()
  let watchersInitialized = false

  // Wait for Auth0 to be ready
  await until(isLoading).toBe(false)

  function setupWatchers() {
    if (watchersInitialized) return
    watchersInitialized = true

    const profileStore = useProfileStore()
    const clinicStore = useClinicStore()

    // Watch for profile changes
    watch(
      () => profileStore.profile,
      profile => {
        setSentryUser(profile, clinicStore.currentClinic)
      },
      { immediate: true }
    )

    // Watch for clinic changes
    watch(
      () => clinicStore.currentClinic,
      clinic => {
        setSentryUser(profileStore.profile, clinic)
      }
    )
  }

  // Set up watchers immediately if already authenticated
  if (isAuthenticated.value) {
    setupWatchers()
  }

  // Watch for authentication changes to initialize when user logs in
  watch(isAuthenticated, authenticated => {
    if (authenticated) {
      setupWatchers()
    } else {
      // Clear Sentry user context when logged out
      setSentryUser(null)
    }
  })
}
